import { FocusEventHandler, useCallback, useState } from 'react';

import { OnInputChange } from '../types';

export interface UseInputProps<Value> {
  addAutocompleteValue: (value: Value[]) => void;
  forbiddenSymbols?: string[];
  separators: string[];
}

export interface UseInputResult<Value> {
  inputValue: string;
  onInputBlur: FocusEventHandler<HTMLDivElement>;
  onInputChange: OnInputChange<Value>;
}

export const useInput = <Value>({
  addAutocompleteValue,
  forbiddenSymbols,
  separators,
}: UseInputProps<Value>): UseInputResult<Value> => {
  const [inputValue, setInputValue] = useState('');

  const onInputChange = useCallback<OnInputChange<Value>>(
    (_event, nextInputValue) => {
      const foundSeparators = separators.filter(separator =>
        nextInputValue.includes(separator),
      );

      const hasSeparators = foundSeparators.length > 0;

      if (hasSeparators) {
        const regExp = new RegExp(foundSeparators.join('|'), 'g');

        const options = nextInputValue.split(regExp).filter(Boolean) as Value[];

        addAutocompleteValue(options);
        setInputValue('');
      } else {
        setInputValue(
          nextInputValue
            .split('')
            .filter(symbol => !forbiddenSymbols?.includes(symbol))
            .join(''),
        );
      }
    },
    [addAutocompleteValue, forbiddenSymbols, separators],
  );

  const onInputBlur = useCallback<FocusEventHandler<HTMLDivElement>>(
    event => {
      // the target is actually a textarea element, but the Autocomplete
      // component expects it to be a div element.
      const target = event.target as unknown as HTMLTextAreaElement;

      const value = target.value.trim() as Value;

      if (value) {
        addAutocompleteValue([value]);
        setInputValue('');
      }
    },
    [addAutocompleteValue],
  );

  return { inputValue, onInputBlur, onInputChange };
};
